/**
 * Browser side of push notifications: registers the service worker
 * (public/sw.js), subscribes this device with the server's VAPID key and
 * keeps the server's copy in sync through /resources/push-subscription.
 * Client-only: every function expects `window`.
 */
import { subscriptionMatchesKey, urlBase64ToUint8Array } from "./pwa";

const SERVICE_WORKER_URL = "/sw.js";
const ENDPOINT = "/resources/push-subscription";
const SYNCED_KEY = "push-subscription-synced";

/** Dispatched on window after this device's subscription changed. */
export const PUSH_CHANGED_EVENT = "pwa:push-changed";

function announceChange() {
  window.dispatchEvent(new Event(PUSH_CHANGED_EVENT));
}

let listeningForNavigation = false;

/** The worker asks the page to load a notification's target when it cannot navigate the window itself. */
function listenForPushNavigation() {
  if (listeningForNavigation) return;
  listeningForNavigation = true;
  navigator.serviceWorker.addEventListener("message", (event) => {
    const { type, url } = event.data ?? {};
    if (type === "push-navigate" && typeof url === "string") {
      const target = new URL(url, window.location.href);
      if (target.origin === window.location.origin) {
        window.location.assign(target.href);
      }
    }
  });
}

export function supportsServiceWorker(): boolean {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

export function supportsPush(): boolean {
  return (
    supportsServiceWorker() &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function notificationPermission(): NotificationPermission | null {
  return typeof window !== "undefined" && "Notification" in window
    ? Notification.permission
    : null;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!supportsServiceWorker()) return null;
  listenForPushNavigation();
  try {
    return await navigator.serviceWorker.register(SERVICE_WORKER_URL, {
      scope: "/",
    });
  } catch {
    return null;
  }
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!supportsServiceWorker()) return null;
  return (
    (await navigator.serviceWorker.getRegistration("/")) ??
    (await registerServiceWorker())
  );
}

export async function getPushSubscription(): Promise<PushSubscription | null> {
  if (!supportsPush()) return null;
  const registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

async function send(body: unknown): Promise<{ ok: boolean; error?: string }> {
  try {
    const response = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    return response.ok
      ? { ok: true }
      : { ok: false, error: data.error ?? "Something went wrong" };
  } catch {
    return { ok: false, error: "You seem to be offline" };
  }
}

function rememberSynced(endpoint: string | null) {
  try {
    if (endpoint) sessionStorage.setItem(SYNCED_KEY, endpoint);
    else sessionStorage.removeItem(SYNCED_KEY);
  } catch {
    // Storage blocked: we just sync again next time.
  }
}

async function subscribeWithKey(
  registration: ServiceWorkerRegistration,
  vapidPublicKey: string,
): Promise<PushSubscription> {
  let subscription = await registration.pushManager.getSubscription();
  if (
    subscription &&
    !subscriptionMatchesKey(
      subscription.options.applicationServerKey,
      vapidPublicKey,
    )
  ) {
    // Made with a VAPID key the server no longer has.
    await subscription.unsubscribe().catch(() => {});
    subscription = null;
  }
  return (
    subscription ??
    registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    })
  );
}

export type EnablePushResult =
  | { ok: true }
  | { ok: false; reason: "denied" | "dismissed" | "error"; error?: string };

/**
 * Asks for permission and subscribes this device. Must be called straight
 * from a click: Safari only shows the permission prompt for a user gesture,
 * which is why the permission request comes before any other await.
 */
export async function enablePush(
  vapidPublicKey: string,
): Promise<EnablePushResult> {
  if (!supportsPush()) {
    return { ok: false, reason: "error", error: "Not supported here" };
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    announceChange();
    return { ok: false, reason: permission === "denied" ? "denied" : "dismissed" };
  }

  try {
    await getRegistration();
    const registration = await navigator.serviceWorker.ready;
    const subscription = await subscribeWithKey(registration, vapidPublicKey);
    const result = await send({
      intent: "subscribe",
      subscription: subscription.toJSON(),
    });
    if (!result.ok) {
      // Don't leave a subscription the server doesn't know about.
      await subscription.unsubscribe().catch(() => {});
      return { ok: false, reason: "error", error: result.error };
    }
    rememberSynced(subscription.endpoint);
    announceChange();
    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      reason: "error",
      error: error instanceof Error ? error.message : "Could not subscribe",
    };
  }
}

/** Unsubscribes this device locally and on the server. */
export async function disablePush(): Promise<{ ok: boolean; error?: string }> {
  const subscription = await getPushSubscription().catch(() => null);
  if (!subscription) return { ok: true };
  const { endpoint } = subscription;
  // Unsubscribe in the browser first: even if the server call fails, the
  // push service then answers 410 and the server drops the device itself.
  await subscription.unsubscribe().catch(() => {});
  rememberSynced(null);
  announceChange();
  return send({ intent: "unsubscribe", endpoint });
}

/** Sends the "notifications are on" test notification to this device. */
export async function sendTestPush(): Promise<{ ok: boolean; error?: string }> {
  const subscription = await getPushSubscription().catch(() => null);
  if (!subscription) return { ok: false, error: "Notifications are off" };
  return send({ intent: "test", endpoint: subscription.endpoint });
}

/**
 * On app start: register the worker and, if this device is subscribed,
 * make sure the server has it for the signed-in account (it may have been
 * dropped, or another account used this device before). Once per session.
 */
export async function syncPushSubscription(
  vapidPublicKey: string | null,
): Promise<void> {
  const registration = await registerServiceWorker();
  if (!registration || !vapidPublicKey || !supportsPush()) return;
  if (Notification.permission !== "granted") return;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  let alreadySynced = false;
  try {
    alreadySynced = sessionStorage.getItem(SYNCED_KEY) === subscription.endpoint;
  } catch {
    // Storage blocked: sync anyway.
  }
  const keyMatches = subscriptionMatchesKey(
    subscription.options.applicationServerKey,
    vapidPublicKey,
  );
  if (alreadySynced && keyMatches) return;

  if (!keyMatches) {
    subscription = await subscribeWithKey(registration, vapidPublicKey);
  }
  const result = await send({
    intent: "subscribe",
    subscription: subscription.toJSON(),
  });
  if (result.ok) rememberSynced(subscription.endpoint);
}
